import {
  isApiResponseError,
  HTTP_STATUS,
  type RequestOptions,
  type ApiError,
  type TransportMiddleware
} from '@brightlocal/api-client';

import { getAccessToken } from './manageAccessToken';
import { refreshAccessToken } from './tokenRefreshService';

/**
 * Transport middleware that ensures an access token is present before each request.
 * 1. Pre-request: if no token in memory (e.g. after page reload), refreshes first.
 * 2. Post-request fallback: on 401, refreshes the token and retries once
 *    (handles expired tokens or server-side revocation).
 */
export const createAuthMiddleware = (): TransportMiddleware => (transport) => {
  const wrapped = async (endpoint: string, options: RequestOptions<unknown>) => {
    if (!getAccessToken()) {
      await refreshAccessToken();
    }

    try {
      return await transport(endpoint, options);
    } catch (error: unknown) {
      const apiError = error as ApiError;

      if (isApiResponseError(apiError) && apiError.status === HTTP_STATUS.AUTHORIZATION_ERROR) {
        await refreshAccessToken();

        return transport(endpoint, options);
      }

      throw error;
    }
  };

  return wrapped as ReturnType<TransportMiddleware>;
};
